import { useEffect, useMemo } from "react";

/**
 * Custom debounce function
 * @param {Function} func - The function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

/**
 * React hook version of debounce that cleans up on unmount
 * @param {Function} callback - The function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const useDebounce = (callback, delay) => {
  const debouncedFn = useMemo(() => debounce(callback, delay), [callback, delay]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFn.cancel = () => clearTimeout(debouncedFn.timeoutId);
    };
  }, [debouncedFn]);

  return debouncedFn;
};